import type { ReactNode } from 'react';
import type { DashboardZeitraumTyp } from '@fameacc/contracts';
import { cn } from '../ui/shadcn/utils';

/**
 * Der Seitenkopf des Dashboards — Begrüssung, Tageskontext-Zeile,
 * Periodenschalter. Auftrag `F-dashlayout` (2026-09-01), Design
 * `docs/scribbles/2026-08-31-dashboard-design.md` Abschnitt 3.
 *
 * **Der Kopf rechnet nichts und ruft nichts ab.** Er bekommt fertige Zahlen
 * und den gewählten Zeitraum von aussen — dieselbe Arbeitsteilung wie bei den
 * beiden Chart-Bausteinen (`berichte/UmsatzAusgabenChart.tsx`): Wer keine
 * eigene Quelle hat, kann auch keine eigene Zahl erfinden.
 *
 * **Eine Zahl, deren Abruf fehlgeschlagen ist, erscheint hier gar nicht.**
 * `null` heisst „keine Auskunft" und führt zum Weglassen des ganzen
 * Satzstücks; `0` ist dagegen eine echte Auskunft und wird ausgeschrieben
 * („keine überfälligen Rechnungen"). Das ist dieselbe Regel, die
 * `apps/web/src/app/navigation.ts` für die Nav-Abzeichen trägt (`E-324`: „wo
 * keine Zahl ist, ist kein Abzeichen") — und der Grund, warum diese Zeile
 * niemals eine Null zeigt, die in Wahrheit ein 403 war.
 *
 * **Kein Umsatzziel und kein Fortschrittsbalken.** Die Frage `DB-1` des
 * Designs (Abschnitt 9) — ob es ein konfigurierbares Jahresumsatzziel geben
 * soll — ist unbeantwortet; es gibt im Bestand keine Quelle dafür. Solange
 * das so ist, wird hier weder ein Ziel gezeigt noch Platz dafür freigehalten:
 * Ein leerer Rahmen mit der Aufschrift „Ziel" ist dieselbe Erfindung wie eine
 * erfundene Zahl darin.
 */

/**
 * Die fünf Zeiträume des Berichts, in der Reihenfolge des Schalters.
 *
 * **`gesamt` seit `W-uebersichtgesamt` (2026-09-10, `E-1115`)** —
 * Auftraggeberwunsch: neben Woche/Monat/Quartal/Jahr auch „insgesamt".
 * Der Wertebereich selbst kommt aus `DashboardZeitraumTyp`
 * (`@fameacc/contracts`) — ein fünfter Eintrag, den der Vertrag nicht
 * kennt, wäre ein Typfehler, kein stiller Aufkleber.
 */
const ZEITRAEUME: readonly (readonly [DashboardZeitraumTyp, string])[] = [
  ['woche', 'Woche'],
  ['monat', 'Monat'],
  ['quartal', 'Quartal'],
  ['jahr', 'Jahr'],
  ['gesamt', 'Gesamt'],
];

const TAGESFORMAT = new Intl.DateTimeFormat('de-DE', {
  timeZone: 'Europe/Berlin',
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

/**
 * Die Begrüssung nach Berliner Ortszeit und nicht nach der Zone des Rechners
 * — dieselbe Begründung wie bei `format.ts::berlinerJetzt()`: Gearbeitet wird
 * in Berlin, auch wenn ein Arbeitsplatz falsch eingestellt ist. „Guten
 * Morgen" um 23 Uhr wäre kein Schaden, aber es wäre falsch, und falsch ist
 * hier die Währung.
 */
export function begruessung(stunde: number): string {
  if (stunde < 11) return 'Guten Morgen';
  if (stunde < 18) return 'Guten Tag';
  return 'Guten Abend';
}

/**
 * Die Stunde in Berliner Ortszeit, aus einem echten Zeitpunkt.
 *
 * **Ausgeführt, seit `app/splash/SplashScreen.tsx` dieselbe Begrüssung
 * zeigt.** Das Startbild nach der Anmeldung grüsst nach derselben Uhr wie
 * der Dashboard-Kopf; eine zweite Zeitzonenrechnung daneben wäre die
 * Gelegenheit, dass eines von beiden um 23 Uhr «Guten Morgen» sagt und das
 * andere nicht.
 */
export function berlinerStunde(jetzt: Date): number {
  const teile = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Berlin',
    hour12: false,
    hour: '2-digit',
  }).formatToParts(jetzt);
  const stunde = teile.find((teil) => teil.type === 'hour')?.value ?? '0';
  // `hour12: false` liefert für Mitternacht je nach Laufzeit „24"; modulo 24
  // macht daraus die 0, die `begruessung()` erwartet.
  return Number(stunde) % 24;
}

/**
 * Die Satzstücke der Tageskontext-Zeile — als reine Funktion, damit die
 * Ehrlichkeitsregel (`null` = weglassen, `0` = ausschreiben) ohne DOM prüfbar
 * ist.
 */
export function tageskontextTeile(lage: {
  readonly ueberfaelligAnzahl: number | null;
  readonly eingangNeuAnzahl: number | null;
}): readonly string[] {
  const teile: string[] = [];

  if (lage.ueberfaelligAnzahl !== null) {
    teile.push(
      lage.ueberfaelligAnzahl === 0
        ? 'keine überfälligen Rechnungen'
        : `${String(lage.ueberfaelligAnzahl)} ${
            lage.ueberfaelligAnzahl === 1 ? 'überfällige Rechnung' : 'überfällige Rechnungen'
          }`,
    );
  }

  if (lage.eingangNeuAnzahl !== null) {
    teile.push(
      lage.eingangNeuAnzahl === 0
        ? 'keine neuen Belege seit gestern'
        : `${String(lage.eingangNeuAnzahl)} ${
            lage.eingangNeuAnzahl === 1 ? 'neuer Beleg' : 'neue Belege'
          } seit gestern`,
    );
  }

  return teile;
}

export function DashboardKopf(props: {
  /** Der Anzeigename der angemeldeten Person, wenn ihn die Seite kennt. */
  readonly anzeigename?: string;
  /** `null`, wenn der Abruf der Offenen Posten nicht geantwortet hat. */
  readonly ueberfaelligAnzahl: number | null;
  /** `null`, wenn der Abruf des Eingangskorbs nicht geantwortet hat. */
  readonly eingangNeuAnzahl: number | null;
  readonly zeitraum: DashboardZeitraumTyp;
  readonly onZeitraum: (zeitraum: DashboardZeitraumTyp) => void;
  /** Nur für Proben: der Zeitpunkt, gegen den Begrüssung und Datum stehen. */
  readonly jetzt?: Date;
}): ReactNode {
  const jetzt = props.jetzt ?? new Date();
  const gruss = begruessung(berlinerStunde(jetzt));
  const teile = tageskontextTeile({
    ueberfaelligAnzahl: props.ueberfaelligAnzahl,
    eingangNeuAnzahl: props.eingangNeuAnzahl,
  });

  return (
    <header className="flex flex-wrap items-start justify-between gap-3">
      <div className="flex flex-col gap-1">
        <h1 className="text-[19px] font-semibold tracking-[-0.02em]">
          {props.anzeigename === undefined ? gruss : `${gruss}, ${props.anzeigename}`}
        </h1>
        <p className="text-[13px] text-muted-foreground">
          {[TAGESFORMAT.format(jetzt), ...teile].join(' · ')}
        </p>
      </div>

      <div
        role="group"
        aria-label="Zeitraum"
        className="flex shrink-0 items-center gap-1 rounded-pille border border-border bg-card p-1"
      >
        {ZEITRAEUME.map(([wert, beschriftung]) => (
          <button
            key={wert}
            type="button"
            aria-pressed={props.zeitraum === wert}
            onClick={() => {
              props.onZeitraum(wert);
            }}
            className={cn(
              'rounded-pille px-3 py-1 text-[12.5px] font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              props.zeitraum === wert
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-secondary hover:text-foreground',
            )}
          >
            {beschriftung}
          </button>
        ))}
      </div>
    </header>
  );
}
